/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { type CSSProperties, memo } from 'react';

import { ResponsivePie } from '@nivo/pie';

import { CIColors, CIFonts } from 'shared';

import { ChartTooltip } from '../../../../components/ChartTooltip/ChartTooltip';
import {
  type ChartDimensions,
  defaultChartDimensions,
  defaultChartMargin,
  defaultChartTheme,
  defaultPieConfig,
  type StorageChartData,
} from '../../../../constants/chartConfig';

interface StoragePieChartProps {
  data: ChartData[];
  dimensions?: ChartDimensions;
}

type ChartData = {
  value: number;
  memory: string;
  name: string;
  id: 'used' | 'available';
  icon: string;
  textStyle: CSSProperties;
  itemStyle: CSSProperties;
};

const transformDataForNivo = (data: ChartData[]): StorageChartData[] => {
  return data.map(item => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: item.id === 'used' ? CIColors.Primary : CIColors.Secondary,
    memory: item.memory,
  }));
};

export const StoragePieChart = memo<StoragePieChartProps>(({ data, dimensions = defaultChartDimensions }) => {
  const nivoData = transformDataForNivo(data);

  const legendItems = data.map(item => {
    const color = item.id === 'used' ? CIColors.Primary : CIColors.Secondary;
    return {
      ...item,
      color,
    };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', overflow: 'visible' }}>
      <div style={{ flex: '0 0 auto', width: `${dimensions.width}px`, height: `${dimensions.height}px` }}>
        <ResponsivePie
          data={nivoData}
          margin={defaultChartMargin}
          {...defaultPieConfig}
          colors={d => d.data.color}
          tooltip={({ datum }) => (
            <ChartTooltip label={String(datum.label)} value={`${datum.value}% ${datum.data.memory}`} />
          )}
          theme={defaultChartTheme}
        />
      </div>
      <div style={{ marginLeft: 32, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {legendItems.map(item => (
          <div key={item.name} style={{ marginBottom: 8, display: 'flex', alignItems: 'baseline' }}>
            <span
              style={{
                color: item.color,
                fontWeight: 700,
                fontSize: 28,
                marginRight: 8,
                fontFamily: CIFonts.Text,
                lineHeight: 1,
              }}
            >
              {item.value}%
            </span>
            <span
              style={{
                color: item.color,
                fontWeight: 700,
                fontSize: 18,
                marginRight: 8,
                fontFamily: CIFonts.Text,
                lineHeight: 1,
              }}
            >
              {item.memory}
            </span>
            <span
              style={{
                color: CIColors.Text,
                fontWeight: 400,
                fontSize: 18,
                fontFamily: CIFonts.Text,
                marginRight: 8,
              }}
            >
              {item.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});
